/**
 * 🔒 FOCUS TRAP HOOK - MOBILE DRAWER & MODAL MANAGEMENT
 *
 * This React hook provides accessible focus management and scroll locking for mobile drawers,
 * modal dialogs, and other UI components that require trapping keyboard focus within them.
 *
 * 🚀 RECENT MOBILE UX IMPROVEMENTS:
 * - ✅ Removed scrollbar width padding compensation to prevent layout shift
 * - ✅ Added dark gray body background (#374151) when trap is active
 * - ✅ No more double padding issues with mobile filter menus
 * - ✅ Clean mobile experience without white flash or layout jumping
 *
 * 🔧 KEY FEATURES:
 * 1. Focus is confined within the specified element when `isActive` is true
 * 2. Tab and Shift+Tab navigation cycles only through focusable elements within the trap
 * 3. Escape key dispatches a custom 'close-focus-trap' event for parent components
 * 4. Screen reader announcements when the trapped element opens and closes
 * 5. Focus is restored to the triggering element when trap closes
 * 6. Background scrolling prevention with mobile optimizations:
 *    - Locks scrolling on both body and documentElement
 *    - NO scrollbar width compensation (prevents layout shift)
 *    - Dark body background for better mobile UX
 *    - Touch event handling (allows scrolling within the modal)
 *    - Prevents zoom on double tap for modal overlays
 *    - Automatically restores all styles on close
 *
 * 📱 MOBILE-FIRST DESIGN:
 * - No padding compensation = No layout shift
 * - Dark background = Better visual continuity with overlays
 * - Touch-friendly with proper event handling
 * - Works seamlessly with UniversalFilterMenu mobile drawer
 *
 * 🔗 USED BY:
 * - ~/components/universal-filter-menu.tsx - Mobile filter drawer
 * - ~/components/side-cart.tsx - Shopping cart drawer
 * - ~/components/product-modal/* - Product modals
 * - ~/components/age-gate.tsx - Age verification modal
 *
 * @param {boolean} isActive - Controls the active state of the focus trap
 * @returns {React.RefObject<HTMLDivElement>} Ref for the focus trap container
 *
 * 🚀 USAGE EXAMPLE:
 * ```typescript
 * function MobileFilterDrawer({ isOpen, onClose }) {
 *   const drawerRef = useFocusTrap(isOpen);
 *
 *   useEffect(() => {
 *     const handleClose = () => onClose();
 *     document.addEventListener('close-focus-trap', handleClose);
 *     return () => document.removeEventListener('close-focus-trap', handleClose);
 *   }, [onClose]);
 *
 *   return isOpen ? (
 *     <div ref={drawerRef} role="dialog" aria-modal="true" aria-label="Filter products">
 *       <UniversalFilterMenu trapFocus={true} onRequestClose={onClose} />
 *     </div>
 *   ) : null;
 * }
 * ```
 */
import { useEffect, useRef, useCallback } from 'react';

// Reference counter for nested modals — only remove scroll-lock when all traps are closed
let scrollLockCount = 0;

export function useFocusTrap(
  isActive: boolean,
  options: { focusContainer?: boolean; announceOnOpen?: boolean } = {},
) {
  // focusContainer: focus the dialog container on open (so the screen reader announces the
  //   dialog itself — name + description — rather than the first control inside it).
  // announceOnOpen: create the hook's own "… opened." live region (default true). Set false
  //   when the caller already announces the dialog another way (e.g. via the container name
  //   + aria-describedby), to avoid a duplicate announcement.
  const { focusContainer = false, announceOnOpen = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const focusableElements = useRef<HTMLElement[]>([]);

  const getFocusableElements = useCallback(() => {
    if (!ref.current) return [];
    return Array.from(
      ref.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    );
  }, []);

  // Prevent touch scrolling on mobile devices, but allow it within modal content
  const preventTouchMove = useCallback((e: TouchEvent) => {
    // Allow scrolling if the touch is inside the modal content
    if (ref.current?.contains(e.target as Node)) {
      return;
    }
    // Prevent scrolling for touches outside the modal
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (isActive) {
      // Scroll lock via CSS class — one classList operation instead of 4 inline style mutations
      scrollLockCount++;
      document.body.classList.add('scroll-locked');
      document.documentElement.classList.add('scroll-locked');

      // Always prevent touch scrolling on mobile (but allow within modal)
      document.addEventListener('touchmove', preventTouchMove, { passive: false });

      // Announce modal/sidebar open (unless the caller opts out to handle it itself)
      let liveRegion: HTMLDivElement | null = null;
      let announcement = '';
      if (announceOnOpen) {
        liveRegion = document.createElement('div');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.setAttribute('style', 'position: absolute; left: -9999px; top: -9999px;');
        document.body.appendChild(liveRegion);

        const elementLabel = ref.current?.getAttribute('aria-label');
        const labelledBy = ref.current?.getAttribute('aria-labelledby');

        if (elementLabel) {
          announcement = `${elementLabel} opened.`;
        } else if (labelledBy) {
          const labelElement = document.getElementById(labelledBy);
          if (labelElement) {
            announcement = `${labelElement.textContent} opened.`;
          }
        } else if (ref.current?.id) {
          announcement = `${ref.current.id} opened.`;
        }

        if (announcement) {
          liveRegion.textContent = announcement;
        }
      }

      focusableElements.current = getFocusableElements();
      let firstElement = focusableElements.current[0];

      if (focusContainer && ref.current) {
        // Focus the dialog container itself so the screen reader announces the dialog
        // (its name + description) instead of the first control (e.g. a close button).
        if (!ref.current.hasAttribute('tabindex')) {
          ref.current.setAttribute('tabindex', '-1');
        }
        (ref.current as HTMLElement).focus();
      } else if (firstElement) {
        firstElement.focus();
      } else if (ref.current) {
        // Fallback: focus the container to keep focus within the trap
        if (!ref.current.hasAttribute('tabindex')) {
          ref.current.setAttribute('tabindex', '-1');
        }
        (ref.current as HTMLElement).focus();
        firstElement = ref.current as HTMLElement;
      }

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          document.dispatchEvent(new Event('close-focus-trap'));
          return;
        }

        if (e.key === 'Tab') {
          // Recompute focusables in case content changed dynamically
          focusableElements.current = getFocusableElements();
          let first = focusableElements.current[0] as HTMLElement | undefined;
          let last = focusableElements.current[focusableElements.current.length - 1] as
            | HTMLElement
            | undefined;

          // If no focusable children, keep focus on the container
          if (!focusableElements.current.length) {
            e.preventDefault();
            if (ref.current) {
              if (!ref.current.hasAttribute('tabindex')) {
                ref.current.setAttribute('tabindex', '-1');
              }
              (ref.current as HTMLElement).focus();
            }
            return;
          }

          if (e.shiftKey) {
            if (
              document.activeElement === first ||
              !ref.current?.contains(document.activeElement)
            ) {
              e.preventDefault();
              last?.focus();
            }
          } else {
            if (document.activeElement === last || !ref.current?.contains(document.activeElement)) {
              e.preventDefault();
              first?.focus();
            }
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('touchmove', preventTouchMove);

        // Decrement counter — only remove scroll lock when all traps are closed
        scrollLockCount--;
        if (scrollLockCount <= 0) {
          scrollLockCount = 0;
          document.body.classList.remove('scroll-locked');
          document.documentElement.classList.remove('scroll-locked');
        }

        if (liveRegion && announcement) {
          const region = liveRegion;
          region.textContent = announcement.replace('opened', 'closed');
          setTimeout(() => {
            region.remove();
          }, 500);
        }
      };
    }
  }, [isActive, getFocusableElements, focusContainer, announceOnOpen]);

  return ref;
}
